// Lazy P&L loader. FinancialPeriods are NOT part of the dataset bootstrap —
// they're need-to-know (crm.financials.view) and bulky (a property carries
// ~13 months × ~40 GL lines) — so they're fetched per property when a
// property workspace opens, and upserted into the store where the existing
// selectors (financialPeriodsForProperty, propertyFinancialPeriod) find them.
//
// A 403 is the NORMAL outcome for users without the financials permission:
// the state reports 'denied' and the Financials tab simply stays empty.

import { useEffect, useState } from 'react';
import { useStore } from '@/store/useStore';
import { useApiToken } from '@/auth/useApiToken';
import { api, ApiError } from '@/api/client';

export type FinancialsLoadState = 'idle' | 'loading' | 'done' | 'denied' | 'error';

// Module-level (not React state): survives unmount/remount so a property
// with zero periods isn't refetched on every visit.
const fetched = new Set<string>();

export function usePropertyFinancials(propertyId: string | undefined): FinancialsLoadState {
  const upsert = useStore((s) => s.upsert);
  const hydrated = useStore((s) => s.hydrated);
  const getToken = useApiToken();
  const [state, setState] = useState<FinancialsLoadState>('idle');

  useEffect(() => {
    if (!propertyId || !hydrated || fetched.has(propertyId)) return;
    let cancelled = false;
    setState('loading');
    void (async () => {
      try {
        const token = await getToken();
        const periods = await api.getFinancials(token, propertyId);
        if (cancelled) return;
        fetched.add(propertyId);
        for (const fp of periods) upsert('financialPeriods', fp);
        setState('done');
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403) {
          fetched.add(propertyId); // permission won't change mid-session
          setState('denied');
        } else {
          setState('error'); // transient — retried on next mount
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, hydrated]);

  return state;
}
